import type { FC } from 'react';
import {
  alpha,
  Box,
  ButtonBase,
  useTheme,
} from '@/shared/ui/mui';
import { useAudienceColors } from '../hooks/useAudienceColors';
import type { Audience } from '../hooks/useAudienceToggle';

interface AudienceToggleProps {
  audience: Audience;
  onToggle: (audience: Audience) => void;
  variant?: 'light' | 'dark';
}

export const AudienceToggle: FC<AudienceToggleProps> = ({
  audience,
  onToggle,
  variant = 'dark',
}) => {
  const theme = useTheme();
  const colors = useAudienceColors(audience);
  const isLight = variant === 'light';

  return (
    <Box
      sx={{
        display: 'flex',
        background: isLight ? theme.palette.grey[100] : alpha(theme.palette.common.white, 0.15),
        borderRadius: '100px',
        p: '3px',
        gap: '2px',
        backdropFilter: isLight ? 'none' : 'blur(4px)',
      }}
    >
      <ButtonBase
        onClick={() => onToggle('clients')}
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 0.75, sm: 1 },
          borderRadius: '100px',
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          fontWeight: 600,
          transition: 'all 0.3s ease',
          ...(audience === 'clients'
            ? {
                bgcolor: colors.main,
                color: theme.palette.common.white,
                boxShadow: `0 2px 8px ${alpha(colors.main, 0.3)}`,
              }
            : {
                bgcolor: 'transparent',
                color: isLight ? theme.palette.grey[500] : alpha(theme.palette.common.white, 0.7),
              }),
        }}
      >
        Clients
      </ButtonBase>
      <ButtonBase
        onClick={() => onToggle('providers')}
        sx={{
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 0.75, sm: 1 },
          borderRadius: '100px',
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          fontWeight: 600,
          transition: 'all 0.3s ease',
          ...(audience === 'providers'
            ? {
                bgcolor: colors.main,
                color: theme.palette.common.white,
                boxShadow: `0 2px 8px ${alpha(colors.main, 0.3)}`,
              }
            : {
                bgcolor: 'transparent',
                color: isLight ? theme.palette.grey[500] : alpha(theme.palette.common.white, 0.7),
              }),
        }}
      >
        Providers
      </ButtonBase>
    </Box>
  );
};
